import {
  Box,
  Card,
  CircularProgress,
} from '@mui/material';
import CaseTaskGrid from 'src/careflow/care-coordination/case/detail/components/CaseTaskGrid';
import { useValueSets } from 'src/careflow/ph-core/fhir-client/fhir-react';
import React from 'react';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import {
  Task,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { WrappedHealthcareService } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import { MenuTaskHandler, TaskHandlerByType, TaskPermissions } from 'src/careflow/helpers/types';
import TaskContextProvider from '../../common/provider';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import useCollapsibleContext from 'src/hooks/useCollapsibleContext';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { TaskOpenFrom } from '../TasksGrid/TaskModal';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';

interface TaskByPathwayProps {
  tasks: WrappedTask[];
  patient: WrappedPatient | null;
  healthCareServices: WrappedHealthcareService[];
  carePlan?: WrappedCarePlan | null;
  serviceRequests: WrappedServiceRequest[];
  rowDblClickHandlers?: TaskHandlerByType[];
  menuTaskHandlers?: MenuTaskHandler[];
  onAssessmentSelect?: (task: Task) => void;
  permissions: TaskPermissions;
  taskActivityDefinitionContext?: string[];
  openFrom?: TaskOpenFrom;
  title?: string;
  conditions?: WrappedCondition[];
  observations?: WrappedObservation[];
  includeSubTasks?: boolean;
  carePlanTaskIds?: string[];
  allTasksEnabled?: boolean;
  filterTasksFromChecklistItem?: {
    listFilter: string[];
    subListFilter: string[];
    owner?: string[];
    status?: string[];
  },
  isWorkflowContext?: boolean;
  childrenButtons?: React.ReactNode;
}
export interface TaskByPathway {
  id?: string | undefined;
  code: string | undefined;
  display?: string | undefined;
}

const pathwayAll: ValueSetComposeIncludeConcept = {
  code: 'all',
  display: 'All',
};

const TasksByPathway = React.memo(
  ({
    patient,
    tasks,
    carePlan,
    healthCareServices,
    serviceRequests,
    menuTaskHandlers,
    rowDblClickHandlers,
    onAssessmentSelect,
    permissions,
    taskActivityDefinitionContext,
    openFrom,
    title,
    conditions,
    observations,
    includeSubTasks,
    carePlanTaskIds,
    allTasksEnabled,
    filterTasksFromChecklistItem,
    isWorkflowContext,
    childrenButtons
  }: TaskByPathwayProps) => {
    // Get pathways
    const [, { isLoading: isPathWaysLoading }] = useValueSets({
      filter: {
        identifier: 'ph-path-ways',
      },
      map: ValueSetWrapper,
    });

    const { handleItem } = useCollapsibleContext();

    const programs = healthCareServices?.reduce<any[]>((acc, e) => {
      const program = e.program?.[0]?.coding?.[0];
      if (program?.code) {
        acc.push({
          id: e.id,
          code: program.code,
          display: program.display,
        });
      }
      return acc;
    }, []);

    const pathwaysList: TaskByPathway[] = [pathwayAll, ...(programs ?? [])];

    return (
      <TaskContextProvider
        menuTaskHandlers={menuTaskHandlers}
        rowDblClickHandlers={rowDblClickHandlers}
        taskActivityDefinitionContext={taskActivityDefinitionContext}
        filterTasksFromChecklistItem={filterTasksFromChecklistItem}
        isWorkflowContext={isWorkflowContext}
      >
        <PermissionsProvider permissions={permissions}>
          <Card>
            {!isPathWaysLoading ? (
              <CaseTaskGrid
                tasks={tasks}
                patient={patient as WrappedPatient}
                carePlan={carePlan}
                healthCareServices={healthCareServices}
                serviceRequests={serviceRequests}
                onAssessmentSelect={onAssessmentSelect}
                pathwaysList={pathwaysList}
                handleItem={handleItem}
                openFrom={openFrom}
                title={title}
                conditions={conditions}
                observations={observations}
                includeSubTasks={includeSubTasks}
                carePlanTaskIds={carePlanTaskIds}
                allTasksEnabled={allTasksEnabled}
                childrenButtons={childrenButtons}
              />
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  my: 2,
                }}
              >
                <CircularProgress size={50} />
              </Box>
            )}
          </Card>
        </PermissionsProvider>
      </TaskContextProvider>
    );
  }
);

export default TasksByPathway;
